import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../components/Breadcrumb";
import MainLayout from "../components/MainLayout";
import ProtectedPermissionPage from "../components/ProtectedPermissionPage";
import useNotification from "../hooks/useNotification";
import event from "../services/event";
import role from "../services/role";
import userService from "../services/users";
import queryDefault from "../utils/queryDefault";
import cookie from "../utils/useCookie";

export default function UserEdit() {
  const notification = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(cookie.getCookie("USER") || "");
  const schema = yup.object().shape({
    role_id: yup.string().required("role is a required field"),
  });
  const [selectedElements, setSelectedElements] = useState<any>([]);
  const form = useForm({
    defaultValues: {
      id: "",
      username: "",
      role_id: "",
      admin_name: user.username,
      events: [],
    },
    resolver: yupResolver(schema),
  });

  const onSelectionChange = (event: any) => {
    const value = event.target.value;
    form.setValue("events", value);
    setSelectedElements(value);
  };

  const updateUser = useMutation("create_user", userService.editUser, {
    onSuccess: () => {
      navigate("/admin/users/");

      notification.onOpen({
        message: "User was successfully updated!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed Update User",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    updateUser.mutate({
      data: values,
    });
  };

  const listEvent = useQuery("list-event", () =>
    event.getAllEvent({ keyword: "" })
  );

  const listRole = useQuery("list-role", () => role.getAllRole());

  useEffect(() => {
    if (id) {
      userService.viewUser(id).then(({ data }) => {
        form.setValue("id", id);
        form.setValue("role_id", data.role_id);
        form.setValue("username", data.username);
        form.setValue("events", data.events);
        setSelectedElements(form.getValues("events"));
      });
    }
  }, [id]);

  return (
    <MainLayout>
      <Breadcrumb
        label="Edit User"
        breadcrumbs={[{ label: "Users", href: "/admin/users" }]}
        href={"/admin/users"}
      />

      <ProtectedPermissionPage acceptPermissions={[3]}>
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Username</FormLabel>
                <Controller
                  name="username"
                  control={form.control}
                  disabled
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Events</FormLabel>
                <Controller
                  name="events"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        multiple
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                        renderValue={(selected: any) => selected.map((id: any) => {
                          var eventName = ""
                          listEvent?.data?.data?.forEach((element: any) => {
                            if (element.id === id) {
                              eventName = element.name
                            }
                          });
                          return eventName;
                        }).join(", ")}
                        onChange={onSelectionChange}
                      >
                        {listEvent?.data?.data?.map((event: any) => (
                          <MenuItem value={event.id} key={event.id}>
                            <ListItemText primary={event.name} />
                            <Checkbox checked={selectedElements.indexOf(event.id) > -1} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Role</FormLabel>
                <Controller
                  name="role_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        {get(listRole, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>

                      <FormHelperText
                        error={Boolean(fieldState.error?.message)}
                      >
                        {fieldState.error?.message}
                      </FormHelperText>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateUser.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
